import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHaptics } from '../../utils/haptics'
import { useNotifications } from '../../contexts/NotificationContext'
import PageLayout from '../common/PageLayout'
import Card from '../common/Card'
import { accountApi } from '../../utils/accountApi'

// Icons
function PlusIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 5v14M5 12h14"/>
    </svg>
  )
}

function AlertTriangleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  )
}

function CheckCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M9 11l3 3L22 4"/>
    </svg>
  )
}

function CalendarIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
  )
}

const CATEGORIES = [
  { id: 'appliance', name: 'Appliances', icon: '🏠' },
  { id: 'plumbing', name: 'Plumbing', icon: '🚿' },
  { id: 'hvac', name: 'HVAC', icon: '❄️' },
  { id: 'electrical', name: 'Electrical', icon: '⚡' },
  { id: 'electronics', name: 'Electronics', icon: '📺' },
  { id: 'furniture', name: 'Furniture', icon: '🛋️' },
  { id: 'other', name: 'Other', icon: '📦' }
]
const EMPTY = { name: '', category: 'appliance', brand: '', model: '', location: '', installed_on: '', last_service_on: '', next_service_due: '', warranty_until: '', condition: 'good', notes: '' }
const input = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900'

function AssetForm({ initial, onSave, onCancel, onRetire, saving }) {
  const [f, setF] = useState({ ...EMPTY, ...Object.fromEntries(Object.entries(initial || {}).map(([k, v]) => [k, v ?? ''])) })
  const set = k => e => setF({ ...f, [k]: e.target.value })
  const date = (k, label) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input type="date" value={f[k]} onChange={set(k)} className={input} />
    </div>
  )
  return (
    <form onSubmit={e => { e.preventDefault(); onSave(Object.fromEntries(Object.keys(EMPTY).map(k => [k, f[k]]))) }} className="space-y-3">
      <input required placeholder="Name, e.g. Water heater" value={f.name} onChange={set('name')} className={input} />
      <div className="grid grid-cols-2 gap-3">
        <select value={f.category} onChange={set('category')} className={input}>
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={f.condition} onChange={set('condition')} className={input}>
          <option value="good">Good</option>
          <option value="needs_attention">Needs attention</option>
          <option value="critical">Critical</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input placeholder="Brand" value={f.brand} onChange={set('brand')} className={input} />
        <input placeholder="Model" value={f.model} onChange={set('model')} className={input} />
      </div>
      <input placeholder="Room, e.g. Kitchen" value={f.location} onChange={set('location')} className={input} />
      <div className="grid grid-cols-2 gap-3">
        {date('installed_on', 'Installed')}
        {date('warranty_until', 'Warranty until')}
        {date('last_service_on', 'Last service')}
        {date('next_service_due', 'Next service due')}
      </div>
      <textarea placeholder="Notes" rows={2} value={f.notes} onChange={set('notes')} className={input} />
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 py-2 bg-slate-100 text-slate-700 text-sm rounded-lg font-medium">Cancel</button>
        <button type="submit" disabled={saving} className="flex-1 py-2 bg-slate-900 text-white text-sm rounded-lg font-medium disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {onRetire && (
        <button type="button" onClick={onRetire} className="w-full py-2 text-xs text-red-600">
          I no longer have this (retire)
        </button>
      )}
    </form>
  )
}

export default function AssetTracker() {
  const navigate = useNavigate()
  const haptics = useHaptics()
  const { showSuccess, showError } = useNotifications()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setAssets(await accountApi.assets())
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const save = async (id, values) => {
    setSaving(true)
    try {
      if (id) await accountApi.updateAsset(id, values)
      else await accountApi.addAsset(values)
      haptics.success()
      showSuccess(id ? 'Asset updated' : 'Asset added')
      setAdding(false)
      setEditingId(null)
      await load()
    } catch (e) {
      haptics.error()
      showError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const retire = async (asset) => {
    if (!window.confirm(`Mark "${asset.name}" as no longer in use? It will be kept in your records.`)) return
    try {
      await accountApi.retireAsset(asset.id)
      showSuccess('Asset retired')
      setEditingId(null)
      await load()
    } catch (e) {
      showError(e.message)
    }
  }

  const categories = [
    { id: 'all', name: 'All Assets', count: assets.length },
    ...CATEGORIES.map(c => ({ ...c, count: assets.filter(a => a.category === c.id).length })).filter(c => c.count > 0)
  ]

  const filteredAssets = selectedCategory === 'all'
    ? assets
    : assets.filter(asset => asset.category === selectedCategory)

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'bg-green-100 text-green-800'
      case 'needs_attention': return 'bg-yellow-100 text-yellow-800'
      case 'critical': return 'bg-red-100 text-red-800'
      default: return 'bg-slate-100 text-slate-800'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good': return <CheckCircleIcon className="text-green-600" />
      case 'needs_attention': return <AlertTriangleIcon className="text-yellow-600" />
      case 'critical': return <AlertTriangleIcon className="text-red-600" />
      default: return null
    }
  }

  // Dates are plain YYYY-MM-DD; show them without a time zone shift
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set'
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const headerAction = (
    <button onClick={() => { setAdding(true); setEditingId(null) }} title="Add an asset" className="p-2 bg-slate-900 text-white rounded-xl touch-manipulation">
      <PlusIcon />
    </button>
  )

  return (
    <PageLayout
      title="Asset Tracker"
      subtitle="Manage your home appliances and equipment"
      headerAction={headerAction}
      onRefresh={load}
      showBackButton={true}
    >
      {adding && (
        <Card>
          <h3 className="font-semibold text-slate-900 mb-3">Add an asset</h3>
          <AssetForm onSave={v => save(null, v)} onCancel={() => setAdding(false)} saving={saving} />
        </Card>
      )}

      {/* Category Filter */}
      {assets.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap touch-manipulation ${
                selectedCategory === category.id
                  ? 'bg-slate-900 text-white'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>
      )}

      {loading && <Card><div className="text-sm text-slate-500">Loading…</div></Card>}
      {error && <Card><div className="text-sm text-red-600">Could not load your assets: {error}</div></Card>}
      {!loading && !error && assets.length === 0 && !adding && (
        <Card>
          <div className="text-center py-10">
            <div className="text-3xl mb-3">🏠</div>
            <h3 className="font-semibold text-slate-900 mb-2">No assets yet</h3>
            <p className="text-sm text-slate-600 mb-4">Add your water heater, AC units and other equipment to keep track of service and warranty dates.</p>
            <button onClick={() => setAdding(true)} className="px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium">Add an asset</button>
          </div>
        </Card>
      )}

      {/* Assets Grid */}
      <div className="space-y-4">
        {filteredAssets.map(asset => (
          <Card key={asset.id} hover={editingId !== asset.id}>
            {editingId === asset.id ? (
              <AssetForm initial={asset} onSave={v => save(asset.id, v)} onCancel={() => setEditingId(null)} onRetire={() => retire(asset)} saving={saving} />
            ) : (
            <div className="flex items-start gap-4">
              <div className="text-2xl">{CATEGORIES.find(c => c.id === asset.category)?.icon || '📦'}</div>

              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-slate-900">{asset.name}</h3>
                    {(asset.brand || asset.model) && <p className="text-sm text-slate-600">{[asset.brand, asset.model].filter(Boolean).join(' ')}</p>}
                    <p className="text-xs text-slate-500">{[asset.location, asset.address_label].filter(Boolean).join(' · ')}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    {getStatusIcon(asset.condition)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.condition)}`}>
                      {asset.condition.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-xs text-slate-600">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <CalendarIcon />
                      <span className="font-medium">Last Service</span>
                    </div>
                    <span>{formatDate(asset.last_service_on)}</span>
                  </div>

                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <CalendarIcon />
                      <span className="font-medium">Next Service</span>
                    </div>
                    <span className={asset.service_overdue ? 'text-yellow-600 font-medium' : ''}>
                      {formatDate(asset.next_service_due)}{asset.service_overdue ? ' (overdue)' : ''}
                    </span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">
                      Warranty: {formatDate(asset.warranty_until)}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          haptics.light()
                          navigate('/ai-inspect', {
                            state: {
                              preSelectedService: asset.category,
                              assetContext: [asset.name, asset.brand, asset.model].filter(Boolean).join(' ')
                            }
                          })
                        }}
                        className="px-3 py-1 text-xs border border-slate-300 rounded-lg hover:bg-slate-50 touch-manipulation"
                      >
                        Service
                      </button>
                      <button
                        onClick={() => { setEditingId(asset.id); setAdding(false) }}
                        className="px-3 py-1 text-xs border border-slate-300 rounded-lg hover:bg-slate-50 touch-manipulation"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </Card>
        ))}
      </div>

      {/* Quick Stats */}
      {assets.length > 0 && (
        <Card>
          <h3 className="font-semibold text-slate-900 mb-4">Quick Stats</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">{assets.filter(a => a.condition === 'good').length}</div>
              <div className="text-xs text-slate-600">Good Condition</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{assets.filter(a => a.condition !== 'good').length}</div>
              <div className="text-xs text-slate-600">Need Attention</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-600">{assets.filter(a => a.service_overdue).length}</div>
              <div className="text-xs text-slate-600">Service Overdue</div>
            </div>
          </div>
        </Card>
      )}
    </PageLayout>
  )
}
